import { QUERY_PROGRESS_FAILED, QUERY_PROGRESS_SUCCEED } from "core/consts";
import {
  activateEUSCMaintenanceBanner,
  isProd,
} from "core/model/utils/featureFlags";
import { pathToUrl } from "core/model/utils/urls";
import { Link } from "ds/ui";
import { useExternalLinkLabel } from "ds_legacy/components/LinkV2";
import React from "react";

import { useA11yMessage } from "containers/A11yStatusProvider/A11yStatusContext";
import { useEnvContext } from "context/EnvContext";
import { useSealdContext } from "core/seald/SealdContext";
import { useHL7ImportStatusContext } from "dsl/atoms/HL7ImportStatusContext";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useTranslations } from "translations";
import { TranslationComposition } from "translations/helpers";
import { useSealdProgress } from "./useSealdProgress";

type ErrorProps = {
  button?: {
    id: string;
    onClick: () => void;
    title: string;
  };
  message: React.JSX.Element | string;
};

export function useBackendNavbarErrors() {
  const translations = useTranslations();
  const [errors, setErrors] = useState<ErrorProps[]>([]);
  const { hasOneWaySync, isHISActivated } = useHL7ImportStatusContext();
  const { checkSealdProgress, sealdProgress } = useSealdProgress();
  const sealdContext = useSealdContext();
  const externalLink = useExternalLinkLabel();
  const { updateA11yMessage } = useA11yMessage();
  const { isProviderSearchApp, isSenderApp } = useEnvContext();

  const getSealdProgressMessage = () => {
    switch (sealdProgress) {
      case QUERY_PROGRESS_SUCCEED:
        return translations.general.banner.refreshSealdSuccess;
      case QUERY_PROGRESS_FAILED:
        return translations.general.banner.refreshSealdFailed;
      default:
        return translations.general.banner.refreshSealdInProgress;
    }
  };

  const updateErrors = useCallback(
    debounce(() => {
      const updatedErrors: ErrorProps[] = activateEUSCMaintenanceBanner
        ? [
            {
              button: {
                id: "eusc-maintenance",
                onClick: () =>
                  setErrors((prev) =>
                    prev.filter(
                      (error) => error.button?.id !== "eusc-maintenance",
                    ),
                  ),
                title: translations.actions.dismiss,
              },
              message: translations.general.banner.euscMaintence,
            },
          ]
        : [];

      // HIS connection unavailable
      if (isSenderApp && isHISActivated && !hasOneWaySync) {
        updatedErrors.push({
          message: translations.patient.kis.tooltipNotConnected,
        });
      }

      // Seald connection unavailable
      if (sealdProgress === QUERY_PROGRESS_FAILED && !isProviderSearchApp) {
        updatedErrors.push({
          button: {
            id: "refresh",
            onClick: () => {
              checkSealdProgress();
              updateA11yMessage(getSealdProgressMessage());
            },
            title: translations.actions.refresh,
          },
          message: (
            <TranslationComposition
              translations={
                translations.general.banner.cta.noSealdConnectionAlert
              }
              withOptions
            >
              {([before, link, after]) => (
                <>
                  {before}
                  <Link
                    aria-label={externalLink(translations.general.itChecklist)}
                    className="text-white data-[focus-visible=true]:outline-white!"
                    href={pathToUrl({
                      pathname: "/diagnostic",
                      search: undefined,
                    })}
                    id="diagnostic"
                    size="sm"
                    target="_blank"
                    underline="always"
                  >
                    {link}
                  </Link>
                  {after}
                </>
              )}
            </TranslationComposition>
          ),
        });
      }

      // Internal Seald connection issue (shared account)
      if (!sealdContext?.isActive && !isProd && !isProviderSearchApp) {
        updatedErrors.push({
          message: translations.general.banner.internalSealdError,
        });
      }

      setErrors(updatedErrors);
    }, 300), // Debounce delay of 300ms
    [
      hasOneWaySync,
      isHISActivated,
      isSenderApp,
      sealdContext?.isActive,
      sealdProgress,
      translations,
      isProviderSearchApp,
    ],
  );

  useEffect(() => {
    updateErrors();

    return () => {
      updateErrors.cancel(); // Cancel debounce on cleanup to prevent memory leaks
    };
  }, [updateErrors]);

  return errors;
}
